import {
  useAssignAiConfigurationToProjectsMutation,
  useUpdateAiConfigurationMutation,
} from '@lokalise/shared'
import type { UpdateAiConfigRequestBody } from '@lokalise/translation-api-schemas'
import { isPromiseRejected } from '../types/promise'
import {
  appHttpClient,
  authenticatedPublicApiHeaderBuilder,
  getTeamIdFromSession,
} from '../utils/api'

type UseUpdateProfileProps = {
  onSuccess: () => void
  onError: (error: unknown) => void
}

type UpdateProps = {
  profile: UpdateAiConfigRequestBody
  projects: string[]
  profileId: string
}

export const useUpdateProfile = ({ onSuccess, onError }: UseUpdateProfileProps) => {
  const teamId = getTeamIdFromSession()

  const { mutateAsync: updateConfig, isPending: isUpdatingAiConfig } =
    useUpdateAiConfigurationMutation(appHttpClient, authenticatedPublicApiHeaderBuilder)

  const { mutateAsync: assignAiConfig, isPending: isAssigningAiConfig } =
    useAssignAiConfigurationToProjectsMutation(appHttpClient, authenticatedPublicApiHeaderBuilder)

  const update = ({ profile, projects, profileId }: UpdateProps) => {
    const updatePromise = updateConfig({ ...profile, teamId, configurationId: profileId })
    const assignPromise = assignAiConfig({
      project_ids: projects,
      teamId,
      configurationId: profileId,
    })

    // Success state only if both requests succeed
    Promise.allSettled([assignPromise, updatePromise]).then((res) => {
      const hasFailedResponse: PromiseRejectedResult | undefined = res.find(isPromiseRejected)

      if (hasFailedResponse) {
        onError(hasFailedResponse.reason)
        return
      }

      onSuccess()
    })
  }

  return { update, isPending: isUpdatingAiConfig || isAssigningAiConfig }
}
